/*
 * A mirror of server/domain/tags/tagString.js, which is the authority.
 *
 * The two must agree exactly. A tag the client treats as new but the server
 * normalises onto one already on the product is how "Cannot have duplicate
 * tags" reaches a user who typed nothing wrong. tagString.test.js runs the
 * server's production corpus vectors against this copy so drift fails a test
 * rather than a save.
 */

const COMBINING_MARKS = /[̀-ͯ]/g;
/* & / , and + join words as surely as a space does, so they become the
 * separator rather than being dropped. Apostrophes and quotes are NOT here:
 * they decorate a word rather than join two, so "baldur's-gate" has to become
 * "baldurs-gate" and not "baldur-s-gate". */
const SEPARATORS = /[\s&/,+]+/g;
const DISALLOWED_CHARACTERS = /[^a-z0-9_-]/g;
const REPEATED_DASHES = /-{2,}/g;
const EDGE_DASHES = /^-+|-+$/g;

/* Trimming the edge dashes must stay last: dropping disallowed characters can
 * expose new ones. Underscores are legal in a tag and are not folded to dashes,
 * because merging genshin_impact into genshin-impact is an admin's decision. */
export const normalizeTagString = (raw = "") =>
  String(raw ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .replace(SEPARATORS, "-")
    .replace(DISALLOWED_CHARACTERS, "")
    .replace(REPEATED_DASHES, "-")
    .replace(EDGE_DASHES, "");

export const toTag = (tagString) => ({
  tagString: normalizeTagString(tagString),
  active: true,
});

/*
 * dedupeTags keeps the first of each normalised tag string. The server rejects
 * a payload carrying two tags that normalise alike, so no editor may build one.
 */
export const dedupeTags = (tags = []) =>
  tags.filter(
    (tag, index) =>
      tags.findIndex(
        (other) =>
          normalizeTagString(other.tagString) ===
          normalizeTagString(tag.tagString),
      ) === index,
  );

/*
 * withSpecialTags folds a product's character and franchise tags into its tag
 * list. Both sides are normalised first: product_tag rows are stored normalised
 * while character_tag_string is not, so comparing them raw reports a tag as
 * missing, adds it again, and hands the server a pair it rejects as duplicates.
 *
 * Returns the list unchanged when there is nothing to fold, so a caller can
 * tell "no change" by length alone.
 */
export const withSpecialTags = ({ tags = [], specialTagStrings = [] }) => {
  const specialTags = specialTagStrings
    .map(normalizeTagString)
    .filter(Boolean)
    .map(toTag);

  return specialTags.length === 0
    ? tags
    : dedupeTags([...tags, ...specialTags]);
};

export default { dedupeTags, normalizeTagString, toTag, withSpecialTags };
